import { Navigation, NavItem } from './nav-item.model';

/** What the navigation depends on, resolved by the sidebar from the user's claims and plan. */
export interface NavigationContext {
    translate: (key: string) => string;
    /** Whether the user holds one of `features`, for the current organization or globally. */
    hasFeature: (features: string[], forOrganization: boolean) => boolean;
    isSudo: boolean;
    isOnboarded: boolean;
    /** Modules unlocked by the organization's pricing plan. */
    licence: { reduce: boolean; solutions: boolean; csrd: boolean; expert: boolean };
    openMeasure: () => void;
    openHelp: () => void;
    resetAdminSelection: () => void;
}

/** Builds the side navigation: entries the user cannot see are left out, not hidden. */
export function buildNavigation(ctx: NavigationContext): Navigation {
    const t = ctx.translate;
    const has = ctx.hasFeature;
    const licenceHint = t('miscellaneous.licenseTooltip');

    const appAccess = has(['organization'], true) || ctx.isSudo;
    const notOnboarded = !ctx.isOnboarded;
    const isAdmin = has(['admin_admin'], false) || has(['admin_admin'], true);
    const isOrganizationAdmin = has(['organization_admin'], false) || has(['organization_admin'], true);
    const isPlatformAdmin = has(['dcarb_admin_admin'], false);

    const app: NavItem[] = appAccess
        ? [
              {
                  id: 'measure',
                  label: t('menu.app.measure'),
                  icon: 'square_foot',
                  // Picked at click time: the scan the user opened from the matrix, else the matrix.
                  action: ctx.openMeasure,
                  activeRoutes: ['/app/measure', '/app/advanced_measure', '/app/measure-summary'],
                  disabled: notOnboarded,
              },
              {
                  id: 'results',
                  label: t('menu.app.results'),
                  icon: 'search_insights',
                  route: '/app/results',
                  exact: false,
                  disabled: notOnboarded,
              },
              {
                  id: 'reduce',
                  label: t('menu.app.reduce'),
                  icon: 'trending_down',
                  disabled: notOnboarded || (!ctx.licence.reduce && !ctx.licence.solutions),
                  disabledHint: licenceHint,
                  children: [
                      {
                          id: 'reduce-advanced',
                          label: t('menu.app.reduceAdvanced'),
                          route: '/app/reduce',
                          exact: false,
                          disabled: !ctx.licence.reduce,
                          disabledHint: licenceHint,
                      },
                      {
                          // Solutions then the action plan, two steps of one page.
                          id: 'reduce-simplified',
                          label: t('menu.app.reduceSimplified'),
                          route: '/app/solutions',
                          exact: false,
                          activeRoutes: ['/app/actionplan'],
                          disabled: !ctx.licence.solutions,
                          disabledHint: licenceHint,
                      },
                  ],
              },
              {
                  id: 'csrd',
                  label: t('menu.app.csrd.title'),
                  icon: 'policy',
                  disabled: notOnboarded || !ctx.licence.csrd,
                  disabledHint: licenceHint,
                  children: [
                      {
                          id: 'csrd-measure',
                          label: t('menu.app.csrd.measure'),
                          route: '/app/csrd/measure',
                          exact: false,
                      },
                      { id: 'csrd-results', label: t('menu.app.csrd.results'), route: '/app/csrd/results' },
                  ],
              },
              {
                  id: 'emission-factors',
                  label: t('menu.app.emissionFactor'),
                  icon: 'library_books',
                  route: '/app/emission-factors',
                  disabled: notOnboarded || !ctx.licence.expert,
                  disabledHint: licenceHint,
              },
              ...(isAdmin
                  ? [
                        {
                            id: 'data-import',
                            label: t('menu.app.dataImport'),
                            icon: 'file_upload',
                            route: '/app/data-import',
                        },
                    ]
                  : []),
          ]
        : [];

    const admin: NavItem[] = [];
    if (isAdmin || isOrganizationAdmin) {
        admin.push({
            id: 'organizations',
            label: t('menu.dedicatedAdmin.home'),
            icon: 'admin_panel_settings',
            // Child routes (an open organisation form) keep the entry highlighted. No selection
            // reset: coming back must restore the admin's organisation list.
            route: '/dedicated_admin/home',
            exact: false,
            disabled: !has(['organization_admin'], false) && !ctx.isSudo,
        });
    }
    if (isAdmin) {
        admin.push({
            id: 'administrators',
            label: t('menu.admin.home'),
            icon: 'shield_person',
            // Shares the organisations console's selection, so it is not reset either.
            route: '/admin/home',
            exact: false,
            disabled: !has(['admin_admin'], false) && !ctx.isSudo && !has(['admin_admin'], true),
        });
    }
    if (isAdmin || isOrganizationAdmin) {
        admin.push({
            id: 'dashboard',
            label: t('menu.dedicatedAdmin.dashboard'),
            icon: 'token',
            route: '/dedicated_admin/dashboard',
            action: ctx.resetAdminSelection,
        });
    }
    if (isPlatformAdmin) {
        admin.push(
            {
                id: 'platform-emission-factors',
                label: t('menu.app.emissionFactor'),
                icon: 'library_books',
                route: '/app/emission-factors',
            },
            {
                id: 'mcp',
                label: t('menu.admin.mcp.title'),
                icon: 'hub',
                children: [
                    { id: 'mcp-usage', label: t('menu.admin.mcp.usage'), route: '/admin/mcp-usage' },
                    {
                        id: 'mcp-internal-access',
                        label: t('menu.admin.mcp.internalAccess'),
                        route: '/admin/mcp-internal-access',
                    },
                ],
            },
        );
    }

    const settingsRoute = has(['admin_admin'], false)
        ? '/admin/settings'
        : has(['organization_admin'], false)
          ? '/dedicated_admin/settings'
          : '/app/settings';

    const account: NavItem[] = [
        { id: 'help', label: t('menu.actions.help'), icon: 'contact_support', action: ctx.openHelp },
        {
            id: 'settings',
            label: t('menu.actions.settings'),
            icon: 'settings',
            route: settingsRoute,
            exact: false,
            disabled: notOnboarded && !has(['organization_admin'], false),
            testId: 'settings-menu-item',
        },
    ];

    return { sections: [app, admin].filter((section) => section.length > 0), account };
}

/** Whether `url` is the page `item` leads to, ignoring the query string and fragment. */
export function isNavItemActive(item: NavItem, url: string): boolean {
    const path = url.split(/[?#]/)[0];
    const matches = (prefix: string, exact: boolean) => path === prefix || (!exact && path.startsWith(prefix + '/'));

    if (item.route && matches(item.route, item.exact ?? true)) {
        return true;
    }
    if (item.activeRoutes?.some((prefix) => matches(prefix, false))) {
        return true;
    }
    return item.children?.some((child) => isNavItemActive(child, url)) ?? false;
}
